import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Settings, Save, Eye, EyeOff, MapPin, Loader2, Link2, Copy, CreditCard, Truck, ToggleLeft, ToggleRight, Smartphone } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showValues, setShowValues] = useState({});
  const [webhookInfo, setWebhookInfo] = useState(null);

  // Location picker state
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [locProvince, setLocProvince] = useState("");
  const [locCity, setLocCity] = useState("");
  const [locDistrict, setLocDistrict] = useState("");
  const [locSubdistrict, setLocSubdistrict] = useState("");
  const [locLoading, setLocLoading] = useState(false);
  const [locNames, setLocNames] = useState({ province: "", city: "", district: "", subdistrict: "" });

  const fetchSettings = useCallback(async () => {
    try { const { data } = await api.get("/settings"); setSettings(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); loadProvinces(); loadWebhook(); }, [fetchSettings]);

  const loadWebhook = async () => {
    try { const { data } = await api.get("/payments/webhook-info"); setWebhookInfo(data); } catch {}
  };

  const loadProvinces = async () => {
    try { const { data } = await api.get("/shipping/provinces"); setProvinces(data.data || []); } catch {}
  };

  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success("URL disalin!"); };

  const onLocProvinceChange = async (val) => {
    setLocProvince(val);
    const p = provinces.find(x => String(x.id) === val);
    setLocNames(n => ({ ...n, province: p?.name || "" }));
    setLocCity(""); setLocDistrict(""); setLocSubdistrict("");
    setCities([]); setDistricts([]); setSubdistricts([]);
    setLocLoading(true);
    try { const { data } = await api.get(`/shipping/cities/${val}`); setCities(data.data || []); } catch {} finally { setLocLoading(false); }
  };

  const onLocCityChange = async (val) => {
    setLocCity(val);
    const c = cities.find(x => String(x.id) === val);
    setLocNames(n => ({ ...n, city: c?.name || "" }));
    setLocDistrict(""); setLocSubdistrict("");
    setDistricts([]); setSubdistricts([]);
    setLocLoading(true);
    try { const { data } = await api.get(`/shipping/districts/${val}`); setDistricts(data.data || []); } catch {} finally { setLocLoading(false); }
  };

  const onLocDistrictChange = async (val) => {
    setLocDistrict(val);
    const d = districts.find(x => String(x.id) === val);
    setLocNames(n => ({ ...n, district: d?.name || "" }));
    setLocSubdistrict("");
    setSubdistricts([]);
    setLocLoading(true);
    try { const { data } = await api.get(`/shipping/subdistricts/${val}`); setSubdistricts(data.data || []); } catch {} finally { setLocLoading(false); }
  };

  const onLocSubdistrictChange = (val) => {
    setLocSubdistrict(val);
    const s = subdistricts.find(x => String(x.id) === val);
    setLocNames(n => ({ ...n, subdistrict: s?.name || "" }));
  };

  const saveShopOrigin = async () => {
    if (!locDistrict) return toast.error("Pilih minimal sampai kecamatan");
    try {
      await api.put("/settings", { key: "shop_origin_district_id", value: locDistrict });
      await api.put("/settings", { key: "shop_origin_label", value: `${locNames.subdistrict ? locNames.subdistrict + ", " : ""}${locNames.district}, ${locNames.city}, ${locNames.province}` });
      toast.success(`Origin toko disimpan: District ID ${locDistrict}`);
      fetchSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateSetting = async (key, value) => {
    try { await api.put("/settings", { key, value }); toast.success("Disimpan"); fetchSettings(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleShow = (key) => setShowValues(p => ({ ...p, [key]: !p[key] }));
  const isSecret = (key) => key.includes("key") || key.includes("secret") || key.includes("token");

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  const currentOrigin = settings.find(s => s.key === "shop_origin_district_id")?.value || "";
  const currentOriginLabel = settings.find(s => s.key === "shop_origin_label")?.value || "";

  const midtransMode = settings.find(s => s.key === "midtrans_mode")?.value || "sandbox";
  const rajaongkirMode = settings.find(s => s.key === "rajaongkir_mode")?.value || "sandbox";
  const edcEnabled = settings.find(s => s.key === "edc_enabled")?.value || "off";

  const toggleMode = async (service, currentMode) => {
    const newMode = currentMode === "sandbox" ? "production" : "sandbox";
    await updateSetting(`${service}_mode`, newMode);
  };

  const groups = {
    "Fonnte WhatsApp": settings.filter(s => s.key.includes("fontee")),
    "Notifikasi": settings.filter(s => s.key === "wa_auto_notify"),
    "Informasi Toko": settings.filter(s => s.key.startsWith("shop_") && !s.key.includes("origin")),
  };

  return (
    <div className="p-6 md:p-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Pengaturan</h1>
        <p className="text-gray-400 text-sm mt-1">Konfigurasi API pihak ketiga dan informasi toko</p>
      </div>

      <div className="space-y-6">
        {/* Midtrans Webhook Info */}
        {webhookInfo && (
          <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="webhook-info">
            <div className="px-5 py-4 border-b border-[#262626] flex items-center gap-2">
              <Link2 size={16} className="text-[#E60000]" />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">URL Midtrans (Webhook & Redirect)</h2>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-gray-400 text-xs mb-3">{webhookInfo.instructions}</p>
              {[
                { label: "Notification URL (Webhook)", url: webhookInfo.webhook_url },
                { label: "Finish Redirect URL", url: webhookInfo.finish_redirect },
                { label: "Unfinish Redirect URL", url: webhookInfo.unfinish_redirect },
                { label: "Error Redirect URL", url: webhookInfo.error_redirect },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="font-mono text-xs text-[#E60000] bg-[#050505] border border-[#262626] px-3 py-2 mt-1 break-all">{item.url}</p>
                  </div>
                  <button onClick={() => copyUrl(item.url)} className="p-2 text-gray-400 hover:text-white hover:bg-[#262626] shrink-0" data-testid={`copy-webhook-${i}`}><Copy size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Midtrans Payment - Mode Toggle + Keys */}
        <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="settings-midtrans">
          <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-[#E60000]" />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Midtrans Payment</h2>
            </div>
            <button onClick={() => toggleMode("midtrans", midtransMode)} className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${midtransMode === "sandbox" ? "border-yellow-500 bg-yellow-500/10 text-yellow-400" : "border-green-500 bg-green-500/10 text-green-400"}`} data-testid="midtrans-mode-toggle">
              {midtransMode === "sandbox" ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
              {midtransMode === "sandbox" ? "SANDBOX" : "PRODUCTION"}
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className={`p-3 border text-xs ${midtransMode === "sandbox" ? "border-yellow-800 bg-yellow-900/10 text-yellow-400" : "border-green-800 bg-green-900/10 text-green-400"}`}>
              Mode aktif: <strong>{midtransMode.toUpperCase()}</strong> — {midtransMode === "sandbox" ? "Gunakan key sandbox dari dashboard.sandbox.midtrans.com" : "Gunakan key production dari dashboard.midtrans.com"}
            </div>
            {midtransMode === "sandbox" ? (
              <>
                {settings.filter(s => s.key === "midtrans_sandbox_server_key" || s.key === "midtrans_sandbox_client_key").map(s => (
                  <SettingRow key={s.key} setting={s} isSecret={isSecret(s.key)} showValue={showValues[s.key]} onToggleShow={() => toggleShow(s.key)} onSave={updateSetting} />
                ))}
              </>
            ) : (
              <>
                {settings.filter(s => s.key === "midtrans_server_key" || s.key === "midtrans_client_key").map(s => (
                  <SettingRow key={s.key} setting={s} isSecret={isSecret(s.key)} showValue={showValues[s.key]} onToggleShow={() => toggleShow(s.key)} onSave={updateSetting} />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Raja Ongkir - Mode Toggle + Keys */}
        <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="settings-rajaongkir">
          <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck size={16} className="text-[#E60000]" />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Raja Ongkir</h2>
            </div>
            <button onClick={() => toggleMode("rajaongkir", rajaongkirMode)} className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${rajaongkirMode === "sandbox" ? "border-yellow-500 bg-yellow-500/10 text-yellow-400" : "border-green-500 bg-green-500/10 text-green-400"}`} data-testid="rajaongkir-mode-toggle">
              {rajaongkirMode === "sandbox" ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
              {rajaongkirMode === "sandbox" ? "SANDBOX" : "PRODUCTION"}
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className={`p-3 border text-xs ${rajaongkirMode === "sandbox" ? "border-yellow-800 bg-yellow-900/10 text-yellow-400" : "border-green-800 bg-green-900/10 text-green-400"}`}>
              Mode aktif: <strong>{rajaongkirMode.toUpperCase()}</strong> — {rajaongkirMode === "sandbox" ? "Gunakan key starter/testing dari rajaongkir.com" : "Gunakan key production dari rajaongkir.com"}
            </div>
            {rajaongkirMode === "sandbox" ? (
              settings.filter(s => s.key === "rajaongkir_sandbox_api_key").map(s => (
                <SettingRow key={s.key} setting={s} isSecret={isSecret(s.key)} showValue={showValues[s.key]} onToggleShow={() => toggleShow(s.key)} onSave={updateSetting} />
              ))
            ) : (
              settings.filter(s => s.key === "rajaongkir_api_key").map(s => (
                <SettingRow key={s.key} setting={s} isSecret={isSecret(s.key)} showValue={showValues[s.key]} onToggleShow={() => toggleShow(s.key)} onSave={updateSetting} />
              ))
            )}
          </div>
        </div>

        {/* EDC Debit/Kredit */}
        <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="settings-edc">
          <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone size={16} className="text-[#E60000]" />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Mesin EDC (Debit/Kredit)</h2>
            </div>
            <button onClick={() => updateSetting("edc_enabled", edcEnabled === "on" ? "off" : "on")} className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${edcEnabled === "on" ? "border-green-500 bg-green-500/10 text-green-400" : "border-gray-600 bg-gray-800/10 text-gray-500"}`} data-testid="edc-toggle">
              {edcEnabled === "on" ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              {edcEnabled === "on" ? "AKTIF" : "NONAKTIF"}
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className={`p-3 border text-xs ${edcEnabled === "on" ? "border-green-800 bg-green-900/10 text-green-400" : "border-gray-700 bg-gray-900/10 text-gray-500"}`}>
              {edcEnabled === "on" ? "Pembayaran EDC aktif — opsi Debit/Kredit tersedia di kasir POS. Kasir memasukkan No TRACE dari mesin EDC." : "Pembayaran EDC nonaktif — opsi Debit/Kredit tidak tampil di kasir POS."}
            </div>
            {settings.filter(s => s.key === "edc_bank_name" || s.key === "edc_merchant_id").map(s => (
              <SettingRow key={s.key} setting={s} isSecret={false} showValue={true} onToggleShow={() => {}} onSave={updateSetting} />
            ))}
          </div>
        </div>

        {/* Shop Origin Location Picker */}
        <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="settings-shop-origin">
          <div className="px-5 py-4 border-b border-[#262626] flex items-center gap-2">
            <MapPin size={16} className="text-[#E60000]" />
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Lokasi Pengirim (Origin Toko)</h2>
          </div>
          <div className="p-5">
            {currentOrigin && (
              <div className="mb-4 bg-[#050505] border border-[#262626] p-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Origin saat ini</p>
                  <p className="text-white text-sm font-semibold">District ID: {currentOrigin}</p>
                  {currentOriginLabel && <p className="text-gray-400 text-xs">{currentOriginLabel}</p>}
                </div>
              </div>
            )}
            <p className="text-gray-400 text-xs mb-4">Pilih lokasi toko untuk kalkulasi ongkos kirim Raja Ongkir. Data berkorelasi dengan ID Raja Ongkir.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400 text-xs uppercase">Provinsi</Label>
                <Select value={locProvince} onValueChange={onLocProvinceChange}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="origin-province"><SelectValue placeholder="Pilih Provinsi..." /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{provinces.map(p => <SelectItem key={p.id} value={String(p.id)} className="text-white text-xs">{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs uppercase">Kota / Kabupaten</Label>
                <Select value={locCity} onValueChange={onLocCityChange} disabled={!locProvince}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="origin-city"><SelectValue placeholder={locProvince ? "Pilih Kota..." : "Pilih provinsi dulu"} /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{cities.map(c => <SelectItem key={c.id} value={String(c.id)} className="text-white text-xs">{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs uppercase">Kecamatan</Label>
                <Select value={locDistrict} onValueChange={onLocDistrictChange} disabled={!locCity}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="origin-district"><SelectValue placeholder={locCity ? "Pilih Kecamatan..." : "Pilih kota dulu"} /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{districts.map(d => <SelectItem key={d.id} value={String(d.id)} className="text-white text-xs">{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs uppercase">Kelurahan</Label>
                <Select value={locSubdistrict} onValueChange={onLocSubdistrictChange} disabled={!locDistrict}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="origin-subdistrict"><SelectValue placeholder={locDistrict ? "Pilih Kelurahan..." : "Pilih kecamatan dulu"} /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{subdistricts.map(s => <SelectItem key={s.id} value={String(s.id)} className="text-white text-xs">{s.name} ({s.zip_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {locDistrict && (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {locNames.subdistrict && `${locNames.subdistrict}, `}{locNames.district}, {locNames.city}, {locNames.province} | <span className="text-[#E60000] font-mono">District ID: {locDistrict}</span>
                </p>
                <Button onClick={saveShopOrigin} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-9 text-xs font-bold uppercase" data-testid="save-origin-btn">
                  {locLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-1" />} Simpan Origin
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Other Settings Groups */}
        {Object.entries(groups).map(([group, items]) => (
          items.length > 0 && (
            <div key={group} className="bg-[#0F0F0F] border border-[#262626]" data-testid={`settings-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="px-5 py-4 border-b border-[#262626] flex items-center gap-2">
                <Settings size={16} className="text-[#E60000]" />
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">{group}</h2>
              </div>
              <div className="p-5 space-y-4">
                {items.map(s => (
                  <SettingRow key={s.key} setting={s} isSecret={isSecret(s.key)} showValue={showValues[s.key]} onToggleShow={() => toggleShow(s.key)} onSave={updateSetting} />
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function SettingRow({ setting, isSecret, showValue, onToggleShow, onSave }) {
  const [value, setValue] = useState(setting.value || "");
  const [changed, setChanged] = useState(false);

  const handleChange = (e) => { setValue(e.target.value); setChanged(true); };
  const handleSave = () => { onSave(setting.key, value); setChanged(false); };

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Label className="text-gray-400 text-xs font-bold uppercase tracking-wider">{setting.description || setting.key}</Label>
        <div className="relative mt-1">
          <Input className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none pr-10" type={isSecret && !showValue ? "password" : "text"} value={value} onChange={handleChange} placeholder={`Masukkan ${setting.description || setting.key}`} data-testid={`setting-${setting.key}`} />
          {isSecret && (
            <button type="button" onClick={onToggleShow} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              {showValue ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
      </div>
      <Button onClick={handleSave} disabled={!changed} className={`h-10 rounded-none px-4 font-bold uppercase text-xs ${changed ? "bg-[#E60000] hover:bg-[#FF1A1A] text-white" : "bg-[#262626] text-gray-500"}`} data-testid={`save-${setting.key}`}>
        <Save size={14} className="mr-1" /> Simpan
      </Button>
    </div>
  );
}
